import { addDays, format, startOfDay, startOfWeek } from 'date-fns';

/** The date ranges above the denials table, as the design lists them. */
export type DenialsRange = 'thisWeek' | 'lastWeek' | 'days30' | 'days60' | 'all';

export interface IDenialsRangeOption {
    range: DenialsRange;
    /** On the control. */
    label: string;
    /** In running text — "7 groups · official CARC text · last 30 days". */
    phrase: string;
}

export const DENIALS_RANGES: ReadonlyArray<IDenialsRangeOption> = [
    { range: 'thisWeek', label: 'This week', phrase: 'this week' },
    { range: 'lastWeek', label: 'Last week', phrase: 'last week' },
    { range: 'days30', label: '30 days', phrase: 'last 30 days' },
    { range: 'days60', label: '60 days', phrase: 'last 60 days' },
    { range: 'all', label: 'All', phrase: 'all time' },
];

export const DEFAULT_DENIALS_RANGE: DenialsRange = 'days30';

/** "All" still needs a start: the report takes one. Earlier than any remit on the platform. */
export const DENIALS_ALL_TIME_START = new Date(2000, 0, 1);

export const denialsRangeOption = (range: DenialsRange): IDenialsRangeOption =>
    DENIALS_RANGES.find(option => option.range === range) || DENIALS_RANGES[2];

/**
 * The window to ask the adjustment report for, as `[start, end)`.
 *
 * The report counts remits by the date they were created (measured against production remits, 2026-09-16).
 *
 * **The end is EXCLUSIVE — settled by measurement on 2026-09-30.** One call over
 * `[2026-09-23, 2026-09-30)` returned 2636; seven separate one-day calls summed to 2636. When this
 * was written that could not be shown — none of the codes checked had a remit on the end date — so
 * every range including today ends tomorrow, which covers today either way. That is still correct,
 * and now for a known reason rather than as a hedge.
 *
 * "Last week" is the half-open week that ends where this week starts. Weeks start on Sunday, the US
 * calendar the practices bill on.
 */
export const denialsReportWindow = (range: DenialsRange, now: Date): { start: Date; end: Date } => {
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    switch (range) {
        case 'thisWeek':
            return { start: startOfWeek(today), end: tomorrow };
        case 'lastWeek': {
            const thisWeek = startOfWeek(today);
            return { start: addDays(thisWeek, -7), end: thisWeek };
        }
        case 'days60':
            return { start: addDays(today, -60), end: tomorrow };
        case 'all':
            return { start: DENIALS_ALL_TIME_START, end: tomorrow };
        case 'days30':
        default:
            return { start: addDays(today, -30), end: tomorrow };
    }
};

/* ------------------------------------------------------------------------------------------------
 * The reworked page: a fixed seven-day lookup
 * ---------------------------------------------------------------------------------------------- */

/** How many days one lookup covers. The design states it on the picker: "Lookups are always 7 days." */
export const DENIALS_WEEK_DAYS = 7;

export type DenialsWeekPreset = 'thisWeek' | 'lastWeek' | 'picked';

/**
 * The seven-day window ending on `lastDay` INCLUSIVE, expressed half-open as `[start, end)`.
 *
 * The picker asks for the last day of the week you want, so a `lastDay` of the 16th covers the 10th
 * to the 16th and the request carries `end` = the 17th. That `+1` is not cosmetic: the report's end
 * is exclusive (measured 2026-09-30 — one 7-day call returned 2636, seven 1-day calls summed to
 * 2636), so passing `lastDay` as `end` would silently ask for six days and under-report every week
 * without anything looking wrong.
 *
 * The reworked page fixes the span at seven days because the aggregate is the whole adjustment
 * surface: a week measured 189 rows and 11.7 s on production, and the payment path behind the
 * drill-down is 893 payments and 44 s unfiltered over the same span.
 */
export const denialsWeekWindow = (lastDay: Date): { start: Date; end: Date } => {
    const last = startOfDay(lastDay);

    return { start: addDays(last, -(DENIALS_WEEK_DAYS - 1)), end: addDays(last, 1) };
};

/** The last day of the week each preset selects. `picked` keeps whatever the calendar chose. */
export const denialsWeekLastDay = (preset: DenialsWeekPreset, now: Date, picked?: Date): Date => {
    const today = startOfDay(now);
    switch (preset) {
        case 'lastWeek':
            return addDays(today, -DENIALS_WEEK_DAYS);
        case 'picked':
            return startOfDay(picked || today);
        case 'thisWeek':
        default:
            return today;
    }
};

/**
 * The window in running text — `Jun 17 – Jun 23`.
 *
 * Renders the INCLUSIVE last day, not the exclusive `end` the request carries. Showing `end` would
 * name a day whose data is not in the table.
 */
export const denialsWeekPhrase = (window: { start: Date; end: Date }): string =>
    `${format(window.start, 'MMM d')} – ${format(addDays(window.end, -1), 'MMM d')}`;

/** A future week has no data and the picker must not offer one. */
export const isDenialsWeekSelectable = (lastDay: Date, now: Date): boolean =>
    startOfDay(lastDay).getTime() <= startOfDay(now).getTime();
